import { CommonModule, formatDate } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  PasAccordionBodyDirective,
  PasAccordionComponent,
  PasDateTimeComponent,
  PasInputFieldComponent,
  PasInputNumberComponent,
  PasLayoutSidebarHolderComponent,
  PasSidebarHandleService,
  PasTextareaComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridModule } from 'ag-grid-angular';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';

import { SupplierDto } from '../../../api/tur-rc/models/supplier-dto';
import { SupplierService } from '../../../api/tur-rc/services/supplier.service';
import { CreatePartsAgreementRestRequest } from '../../../api/tur-wr/models';
import { PartsAgreementService } from '../../../api/tur-wr/services';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { FieldMasterDataRoutingService } from '../../../services/details-view-service/supplier-parts-routing.service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { ASTERISK } from '../recourse-criteria/recourse-criteria.store';
import { commentCharactersValidator } from '../validator/comment-characters-validator';

import { customTechnicalFactorValidator } from './validator/custom-technical-factor-validator';
import { customPartsAgreementValidator } from './validator/custom.validator';

@Component({
  selector: 'app-parts-agreement-add',
  templateUrl: './parts-agreement-add.component.html',
  styleUrls: ['./parts-agreement-add.component.scss'],
  imports: [
    AgGridModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    TranslatePipe,
    PasAccordionComponent,
    PasAccordionBodyDirective,
    PasInputFieldComponent,
    PasInputNumberComponent,
    PasDateTimeComponent,
    PasTextareaComponent,
    PasLayoutSidebarHolderComponent,
  ],
})
export class PartsAgreementAddComponent implements OnInit {
  private readonly fieldMasterdataRoutingService = inject(FieldMasterDataRoutingService);
  private readonly partsAgreementService = inject(PartsAgreementService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly supplierService = inject(SupplierService);
  private readonly userService = inject(UserService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly translateService = inject(TranslateService);

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output('onBtnCancel') public onBtnCancelEE = new EventEmitter();

  // Date for datetime-picker min & max date as NgbDateStruct
  public readonly tomorrow = this.dateAsObject(moment().add(1, 'days').toDate());
  public readonly longTimeAhead = this.dateAsObject(moment('9999-12-31T12:00:00').toDate());

  // NotificationExceptions
  private static readonly HTTP_STATUS_OK: number = 200;
  private static readonly HTTP_STATUS_UNPROCESSABLE_ENTITY: number = 422;
  private static readonly ERROR_CODE_CRITERIA_NOT_MET: number = 15_009;

  // PartNumber can't start with space character, hyphen(-) and slash(/), but can contain spaces, hyphen(-) and slash(/)
  private static readonly CONST_REG_PART_NUMBER: string = '[a-zA-Z0-9][a-zA-Z0-9 \\-\\/]*';
  // OrderType can't start or end with space character, but can contain spaces, or can be just an asterisk
  private static readonly CONST_REG_ORDER_TYPE: string = '^\\*$|^[A-Za-z0-9]{3,6}$';
  private static readonly COMP_PRODUCTION_DATE_FROM_DEFAULT: string = '1970-01-01T12:00:00';
  private static readonly COMP_PRODUCTION_DATE_TO_DEFAULT: string = '9999-12-31T12:00:00';
  private static readonly COMP_VALIDITY_DATE_FROM_DEFAULT: string = moment().add(1, 'days').toISOString();
  private static readonly COMP_VALIDITY_DATE_TO_DEFAULT: string = '9999-12-31T12:00:00';
  private static readonly COMP_PART_NUMBER_MIN_LENGTH: number = 9;
  private static readonly COMP_MODEL_YEAR_FROM_DEFAULT: number = 1;
  private static readonly COMP_MODE_YEAR_TO_DEFAULT: number = 9999;
  private static readonly COMP_MODEL_YEAR_MIN_LENGTH: number = 1;
  private static readonly COMP_TECHNICAL_FACTOR_DEFAULT: number = 0;

  public readonly compModelYearMaxLength: number = 4;
  public readonly compPartNumberMaxLength: number = 11;
  public readonly compOrderTypeMaxLength: number = 6;
  public readonly compCommentCharactersMaxLength: number = 1000;
  public readonly canAddAndEditFactorCriteria: boolean;

  public formGroup: UntypedFormGroup;
  public decimalSeparator: ',' | '.' = '.';

  public compCommentCharactersLeft = 1000;

  public constructor() {
    this.canAddAndEditFactorCriteria = this.userService.hasRight(UserRights.RECOURSE_BOOKING);
  }

  public ngOnInit(): void {
    this.initializeView();
  }

  public initializeView(): void {
    this.enableSidebar();
    this.decimalSeparator = this.userService.userLanguage.startsWith('en') ? '.' : ',';
    this.setFormValues();
    this.setupOrderTypeDefaultValue();
  }

  /**
   * Sets up a listener to reset the orderType field to asterisk if it becomes empty
   * This ensures we always have a value for orderType
   */
  private setupOrderTypeDefaultValue(): void {
    this.formGroup.get('formCtrlOrderType').valueChanges.subscribe(value => {
      if (value === '' || value === null || value === undefined) {
        this.formGroup.get('formCtrlOrderType').setValue(ASTERISK);
      }
    });
  }

  public onBtnCancel(): void {
    this.onBtnCancelEE.emit();
  }

  public onBtnSave(): void {
    this.spinner.show();

    this.partsAgreementService.createPartsAgreement({ body: this.getSupplierPart() }).subscribe({
      next: resObj => {
        this.doShowNotification(PartsAgreementAddComponent.HTTP_STATUS_OK, null, resObj.id.toString());
        this.spinner.hide();
        this.fieldMasterdataRoutingService.routeToTabPartsAgreement();
      },
      error: err => {
        this.doShowNotification(PartsAgreementAddComponent.HTTP_STATUS_UNPROCESSABLE_ENTITY, err.error.errorCode, null);
        this.spinner.hide();
      },
    });
  }

  private doShowNotification(httpStatus: number, errorCodeNumber: number, message: string): void {
    // Check httpStatus
    switch (httpStatus) {
      // 200 -> ok
      case PartsAgreementAddComponent.HTTP_STATUS_OK: {
        this.notificationServiceWrapper.addSuccessNotification(
          this.translateService.instant('SAVED_TITLE'),
          this.translateService.instant('partsAgreement.httpCode200', { 0: message }),
        );
        break;
      }
      // 422 -> seems to be a businessException
      case PartsAgreementAddComponent.HTTP_STATUS_UNPROCESSABLE_ENTITY: {
        this.doShowNotificationsWithErrorCode(errorCodeNumber);
        break;
      }
      default: {
        // For all other httpStatus != (200 || 422)
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_HEADER'),
          `${message} - ${this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_MESSAGE')}`,
        );
        break;
      }
    }
  }

  private doShowNotificationsWithErrorCode(errorCodeNumber: number): void {
    switch (errorCodeNumber) {
      // 15009 can happen and user get informed
      case PartsAgreementAddComponent.ERROR_CODE_CRITERIA_NOT_MET: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_SAVE_ERROR_HEADER'),
          `${this.translateService.instant('partsAgreement.addErrorCode15009')} (ErrorCodeNumber:${errorCodeNumber})`,
        );
        break;
      }
      // all others businessException should not happen because of ui validation.
      default: {
        this.notificationServiceWrapper.addErrorNotification(
          this.translateService.instant('NOTIFICATION_UNEXPECTED_ERROR_HEADER'),
          `${this.translateService.instant(
            'NOTIFICATION_UNEXPECTED_ERROR_MESSAGE',
          )} (ErrorCodeNumber:${errorCodeNumber})`,
        );
        break;
      }
    }
  }

  public onSupplierChanged(): void {
    const supplierControl: any = this.formGroup.get('formCtrlSupplier');
    const supplierValue: string = supplierControl.value;
    if (isEmpty(supplierValue) || supplierValue.length < 6) {
      this.clearSupplierData();
    } else {
      this.supplierService.getSupplier({ supplierId: supplierValue }).subscribe({
        next: supplierData => {
          if (supplierData && supplierData.name) {
            this.populateSupplierData(supplierData);
          } else {
            supplierControl.setErrors({ incorrect: true });
            this.clearSupplierData();
          }
        },
        error: () => {
          supplierControl.setErrors({ incorrect: true });
          this.clearSupplierData();
        },
      });
    }
  }

  public onCommentChanged(): void {
    const currentCommentVal: string = this.formGroup.get('formCtrlComment').value;
    this.compCommentCharactersLeft = this.compCommentCharactersMaxLength - currentCommentVal.length;
  }

  public isSaveEnabled(): boolean {
    return this.formGroup && this.formGroup.dirty && this.formGroup.valid;
  }

  public enableSidebar(): void {
    this.sidebarHandleService.enableSidebarForView();
  }

  private getSupplierPart(): CreatePartsAgreementRestRequest {
    const supplierNumber: string = this.formGroup.get('formCtrlSupplier').value;
    const sparePartNumber: string = this.formGroup.get('formCtrlPartNumber').value;
    const technicalFactor = Number(this.formGroup.get('formCtrlTechnicalFactor').value) / 100;
    const orderType: string = this.formGroup.get('formCtrlOrderType').value;
    const modelYearFrom = Number(this.formGroup.get('formCtrlModelYearFrom').value);
    const modelYearTo = Number(this.formGroup.get('formCtrlModelYearTo').value);
    const productionDateFrom = this.formatDate(this.formGroup.get('formCtrlProductionDateFrom').value);
    const productionDateTo = this.formatDate(this.formGroup.get('formCtrlProductionDateTo').value);
    const validityDateFrom = this.formatDate(this.formGroup.get('formCtrlValidityDateFrom').value);
    const validityDateTo = this.formatDate(this.formGroup.get('formCtrlValidityDateTo').value);
    const comment: string = this.formGroup.get('formCtrlComment').value;

    return {
      comment,
      modelYearFrom,
      modelYearTo,
      orderType,
      sparePartNumber,
      productionDateFrom,
      productionDateTo,
      supplierNumber,
      technicalFactor,
      validityDateFrom,
      validityDateTo,
    };
  }

  private formatDate(dateAsString: string): string {
    return formatDate(new Date(dateAsString), 'yyyy-MM-dd', this.userService.userLanguage);
  }

  private setFormValues(): void {
    this.formGroup = this.formBuilder.group(
      {
        formCtrlSupplier: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
        formCtrlName: [''],
        formCtrlCity: [''],
        formCtrlModelYearFrom: [
          PartsAgreementAddComponent.COMP_MODEL_YEAR_FROM_DEFAULT,
          [
            Validators.required,
            Validators.minLength(PartsAgreementAddComponent.COMP_MODEL_YEAR_MIN_LENGTH),
            Validators.maxLength(this.compModelYearMaxLength),
          ],
        ],
        formCtrlModelYearTo: [
          PartsAgreementAddComponent.COMP_MODE_YEAR_TO_DEFAULT,
          [
            Validators.required,
            Validators.minLength(PartsAgreementAddComponent.COMP_MODEL_YEAR_MIN_LENGTH),
            Validators.maxLength(this.compModelYearMaxLength),
          ],
        ],
        formCtrlPartNumber: [
          '',
          [
            Validators.required,
            Validators.minLength(PartsAgreementAddComponent.COMP_PART_NUMBER_MIN_LENGTH),
            Validators.maxLength(this.compPartNumberMaxLength),
            Validators.pattern(PartsAgreementAddComponent.CONST_REG_PART_NUMBER),
          ],
        ],
        formCtrlTechnicalFactor: [
          PartsAgreementAddComponent.COMP_TECHNICAL_FACTOR_DEFAULT,
          [Validators.required, customTechnicalFactorValidator.bind(this)],
        ],
        formCtrlOrderType: [
          ASTERISK,
          [Validators.required, Validators.pattern(PartsAgreementAddComponent.CONST_REG_ORDER_TYPE)],
        ],
        formCtrlProductionDateFrom: [PartsAgreementAddComponent.COMP_PRODUCTION_DATE_FROM_DEFAULT, Validators.required],
        formCtrlProductionDateTo: [PartsAgreementAddComponent.COMP_PRODUCTION_DATE_TO_DEFAULT, Validators.required],
        formCtrlValidityDateFrom: [PartsAgreementAddComponent.COMP_VALIDITY_DATE_FROM_DEFAULT, Validators.required],
        formCtrlValidityDateTo: [PartsAgreementAddComponent.COMP_VALIDITY_DATE_TO_DEFAULT, Validators.required],
        formCtrlComment: ['', [Validators.maxLength(this.compCommentCharactersMaxLength), commentCharactersValidator]],
      },
      {
        validators: [
          customPartsAgreementValidator.numberValidator('formCtrlModelYearFrom', 'formCtrlModelYearTo'),
          customPartsAgreementValidator.dateValidator('formCtrlProductionDateFrom', 'formCtrlProductionDateTo'),
          customPartsAgreementValidator.dateValidator('formCtrlValidityDateFrom', 'formCtrlValidityDateTo'),
        ],
      },
    );
  }

  private clearSupplierData(): void {
    this.formGroup.get('formCtrlName').setValue('');
    this.formGroup.get('formCtrlCity').setValue('');
  }

  private populateSupplierData(supplierData: SupplierDto): void {
    this.formGroup.get('formCtrlName').setValue(supplierData.name);
    this.formGroup.get('formCtrlCity').setValue(supplierData.city);
  }

  private dateAsObject(date: Date): { year: number; month: number; day: number } {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Months are 0-indexed, so we add 1
    const day = date.getDate();
    return { year, month, day };
  }
}
